import { useState } from 'react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Download, Search, X } from 'lucide-react';
import axios from 'axios';
import ExportDropdown from '@/components/ExportDropdown';
import { useMemo } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function AccountingTransactions({ transactions, chartOfAccounts, isAdmin, fiscalYear, loadData }) {
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [transactionForm, setTransactionForm] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    account_id: '',
    debit: '',
    credit: '',
    category: '',
    reference: '',
    vendor: ''
  });

  const resetTransactionForm = () => {
    setTransactionForm({
      date: new Date().toISOString().split('T')[0],
      description: '',
      account_id: '',
      debit: '',
      credit: '',
      category: '',
      reference: '',
      vendor: ''
    });
  };

  const createTransaction = async () => {
    if (!transactionForm.account_id || !transactionForm.description) {
      toast.error('Please fill in required fields');
      return;
    }
    const token = getToken();
    try {
      await axios.post(`${API}/api/accounting/transactions`, {
        ...transactionForm,
        debit: parseFloat(transactionForm.debit) || 0,
        credit: parseFloat(transactionForm.credit) || 0,
        source: 'manual'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transaction created');
      setShowTransactionModal(false);
      resetTransactionForm();
      loadData();
    } catch (err) {
      toast.error('Failed to create transaction');
    }
  };

  const updateTransaction = async () => {
    if (!transactionForm.account_id || !transactionForm.description) {
      toast.error('Please fill in required fields');
      return;
    }
    const token = getToken();
    try {
      await axios.put(`${API}/api/accounting/transactions/${editingTransactionId}`, {
        ...transactionForm,
        debit: parseFloat(transactionForm.debit) || 0,
        credit: parseFloat(transactionForm.credit) || 0
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Transaction updated');
      setShowTransactionModal(false);
      setEditingTransactionId(null);
      resetTransactionForm();
      loadData();
    } catch (err) {
      toast.error('Failed to update transaction');
    }
  };

  const deleteTransaction = async (transactionId) => {
    const token = getToken();
    try {
      await axios.delete(`${API}/api/accounting/transactions/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transaction deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete transaction');
    }
  };

  const bulkDeleteTransactions = async () => {
    if (selectedTransactionIds.size === 0) return;
    const token = getToken();
    try {
      await axios.post(`${API}/api/accounting/transactions/bulk-delete`, {
        ids: Array.from(selectedTransactionIds)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Deleted ${selectedTransactionIds.size} transaction(s)`);
      setSelectedTransactionIds(new Set());
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      toast.error('Failed to delete transactions');
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget === 'bulk') {
      bulkDeleteTransactions();
    } else if (deleteTarget) {
      deleteTransaction(deleteTarget);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  const openEditTransaction = (transaction) => {
    setEditingTransactionId(transaction.id);
    setTransactionForm({
      date: transaction.date || new Date().toISOString().split('T')[0],
      description: transaction.description || '',
      account_id: transaction.account_id || '',
      debit: transaction.debit?.toString() || '',
      credit: transaction.credit?.toString() || '',
      category: transaction.category || '',
      reference: transaction.reference || '',
      vendor: transaction.vendor || ''
    });
    setShowTransactionModal(true);
  };

  const toggleTransactionSelection = (id) => {
    setSelectedTransactionIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const toggleAllTransactions = () => {
    if (selectedTransactionIds.size === filteredTransactions.length) {
      setSelectedTransactionIds(new Set());
    } else {
      setSelectedTransactionIds(new Set(filteredTransactions.map(t => t.id)));
    }
  };

  // Search/filter logic
  const filteredTransactions = useMemo(() => {
    if (!searchQuery.trim()) return transactions;
    const q = searchQuery.toLowerCase();
    return transactions.filter(t =>
      (t.description || '').toLowerCase().includes(q) ||
      (t.account_name || '').toLowerCase().includes(q) ||
      (t.vendor || '').toLowerCase().includes(q) ||
      (t.category || '').toLowerCase().includes(q) ||
      (t.source || '').toLowerCase().includes(q) ||
      (t.reference || '').toLowerCase().includes(q) ||
      (t.date || '').includes(q) ||
      String(t.debit || '').includes(q) ||
      String(t.credit || '').includes(q)
    );
  }, [transactions, searchQuery]);

  // Export column definitions for accounting transactions
  const accountingExportColumns = [
    { key: 'date', label: 'Date' },
    { key: 'description', label: 'Description' },
    { key: 'account_name', label: 'Account' },
    { key: 'vendor', label: 'Vendor' },
    { key: 'category', label: 'Category' },
    { key: 'debit', label: 'Debit', format: 'currency' },
    { key: 'credit', label: 'Credit', format: 'currency' },
    { key: 'source', label: 'Source' },
    { key: 'reference', label: 'Reference' },
  ];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Transactions</CardTitle>
            <CardDescription>
              {searchQuery ? `${filteredTransactions.length} of ${transactions.length}` : transactions.length} records for {fiscalYear}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <ExportDropdown
              filteredData={filteredTransactions}
              allData={transactions}
              columns={accountingExportColumns}
              filenamePrefix="accounting_transactions"
              title={`Accounting Transactions - FY ${fiscalYear}`}
            />
            {isAdmin && selectedTransactionIds.size > 0 && (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => { setDeleteTarget('bulk'); setShowDeleteConfirm(true); }}
                data-testid="bulk-delete-transactions-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete Selected ({selectedTransactionIds.size})
              </Button>
            )}
            {isAdmin && (
              <Button onClick={() => { resetTransactionForm(); setEditingTransactionId(null); setShowTransactionModal(true); }} data-testid="add-transaction-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Transaction
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Search Bar */}
          <div className="relative mb-4" data-testid="txn-search-container">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by description, account, vendor, category, source, amount..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
              data-testid="txn-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="txn-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && (
                    <TableHead className="w-10">
                      <input type="checkbox" checked={selectedTransactionIds.size === filteredTransactions.length && filteredTransactions.length > 0} onChange={toggleAllTransactions} className="rounded border-slate-300" />
                    </TableHead>
                  )}
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead>Source</TableHead>
                  {isAdmin && <TableHead className="w-24">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 11 : 9} className="text-center py-8 text-slate-500">
                      {searchQuery ? 'No transactions match your search.' : 'No transactions. Click "Sync Data" to import from Mortgage and Insurance divisions.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map(trans => (
                    <TableRow key={trans.id} data-transaction-id={trans.id} className={selectedTransactionIds.has(trans.id) ? 'bg-blue-50' : ''}>
                      {isAdmin && (
                        <TableCell>
                          <input type="checkbox" checked={selectedTransactionIds.has(trans.id)} onChange={() => toggleTransactionSelection(trans.id)} className="rounded border-slate-300" />
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-sm">{formatDate(trans.date)}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={trans.description}>{trans.description}</TableCell>
                      <TableCell>{trans.account_name}</TableCell>
                      <TableCell className="max-w-[120px] truncate" title={trans.vendor || ''}>
                        {trans.vendor || <span className="text-slate-400">-</span>}
                      </TableCell>
                      <TableCell>{trans.category && <Badge variant="outline">{trans.category}</Badge>}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{trans.debit > 0 ? formatCurrency(trans.debit) : '-'}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{trans.credit > 0 ? formatCurrency(trans.credit) : '-'}</TableCell>
                      <TableCell><Badge variant="secondary" className="capitalize text-xs">{trans.source}</Badge></TableCell>
                      {isAdmin && (
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditTransaction(trans)} title="Edit">
                              <Edit2 className="w-4 h-4 text-blue-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setDeleteTarget(trans.id); setShowDeleteConfirm(true); }} title="Delete">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Modal */}
      <Dialog open={showTransactionModal} onOpenChange={(open) => { 
        setShowTransactionModal(open); 
        if (!open) { setEditingTransactionId(null); resetTransactionForm(); }
      }}>
        <DialogContent data-testid="transaction-modal">
          <DialogHeader>
            <DialogTitle>{editingTransactionId ? 'Edit Transaction' : 'Add Transaction'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={transactionForm.date} onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Account *</Label>
                <Select value={transactionForm.account_id} onValueChange={(v) => setTransactionForm({ ...transactionForm, account_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>
                    {chartOfAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.account_number} - {acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input value={transactionForm.description} onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })} placeholder="Transaction description" />
            </div>
            <div className="space-y-2">
              <Label>Vendor</Label>
              <Input value={transactionForm.vendor} onChange={(e) => setTransactionForm({ ...transactionForm, vendor: e.target.value })} placeholder="e.g., Supplier name, Payee" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Debit (Income)</Label>
                <Input type="number" step="0.01" value={transactionForm.debit} onChange={(e) => setTransactionForm({ ...transactionForm, debit: e.target.value })} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Credit (Expense)</Label>
                <Input type="number" step="0.01" value={transactionForm.credit} onChange={(e) => setTransactionForm({ ...transactionForm, credit: e.target.value })} placeholder="0.00" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Input value={transactionForm.category} onChange={(e) => setTransactionForm({ ...transactionForm, category: e.target.value })} placeholder="e.g., Office Supplies" />
              </div>
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input value={transactionForm.reference} onChange={(e) => setTransactionForm({ ...transactionForm, reference: e.target.value })} placeholder="e.g., Invoice #, Check #" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowTransactionModal(false); setEditingTransactionId(null); resetTransactionForm(); }}>Cancel</Button>
            <Button onClick={editingTransactionId ? updateTransaction : createTransaction}>
              {editingTransactionId ? 'Update Transaction' : 'Create Transaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              {deleteTarget === 'bulk' 
                ? `Are you sure you want to delete ${selectedTransactionIds.size} selected transaction(s)? This action cannot be undone.`
                : 'Are you sure you want to delete this transaction? This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              <Trash2 className="w-4 h-4 mr-2" />
              {deleteTarget === 'bulk' ? `Delete ${selectedTransactionIds.size} Transaction(s)` : 'Delete Transaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
